import {
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from '@tanstack/react-query';
import {
  excludeAccountAlert,
  excludeOrganizationAlert,
  removeAccountAlertExclusion,
  removeOrganizationAlertExclusion,
} from '@kirin/api-client/api/alerts';
import type { ExcludedAlertState } from '@kirin/api-client/contracts/alerts';
import { apiClient } from '@/lib/api-client';
import {
  applyOptimisticPatch,
  cancelOverlappingQueries,
  invalidateAffectedQueries,
  restoreSnapshot,
  type AlertSuppressionScope,
  type CacheSnapshot,
} from './alertSuppressionCache';

/**
 * Exclude scope discriminator. Re-exported as a type alias of
 * {@link AlertSuppressionScope} so existing call sites importing
 * `AlertExcludeScope` keep compiling without churn.
 *
 * - `organization`: scopes the mutation to an org-shared alert. Caller must
 *   already be authorized (owner/manager) by the time the hook is called.
 * - `account`: scopes the mutation to the authenticated user's own alerts.
 */
export type AlertExcludeScope = AlertSuppressionScope;

/** Variables for {@link useExcludeAlert}. */
export interface ExcludeAlertVariables {
  alertKey: string;
  /** ISO datetime in the future, or `null` to exclude indefinitely. */
  excludedUntil: string | null;
}

/** Variables for {@link useRemoveAlertExclusion}. */
export interface RemoveAlertExclusionVariables {
  alertKey: string;
}

/**
 * Canonical "not excluded" projection. Mirrors the backend
 * `NOT_EXCLUDED_DEFAULT` exported from
 * `apps/backend/src/domain/handlers/alerts/alerts-helpers.ts`.
 *
 * Used to optimistically restore the unexcluded shape during
 * `useRemoveAlertExclusion` and as the rollback target. The shape MUST stay
 * aligned with `excludedAlertStateSchema` from
 * `@kirin/api-client/contracts/alerts`.
 */
export const NOT_EXCLUDED_DEFAULT: ExcludedAlertState = Object.freeze({
  excluded: false,
  excludedAt: null,
  excludedUntil: null,
  excludedByUserId: null,
  excludedByEmail: null,
  excludedByName: null,
});

/**
 * Build the optimistic `ExcludedAlertState` shown to the user between
 * "click exclude" and the server response.
 *
 * The optimistic shape uses placeholder identity fields (`null` everywhere
 * except `excluded`, `excludedAt`, `excludedUntil`) because the actor's
 * email/name are not on the client; the server reconciles those via the
 * standard invalidation pass on `onSettled`.
 */
export function buildOptimisticExclude(
  excludedUntil: string | null
): ExcludedAlertState {
  return {
    excluded: true,
    excludedAt: new Date().toISOString(),
    excludedUntil,
    excludedByUserId: null,
    excludedByEmail: null,
    excludedByName: null,
  };
}

/**
 * React Query mutation hook to exclude a grouped alert (KIR-2363 + KIR-2447).
 *
 * Optimistically patches the alert's `exclude` fragment in every
 * grouped-alerts list and alert-occurrences cache entry in scope, captures
 * the affected snapshots, and rolls them back on error. Always invalidates
 * lists, occurrences, and (org-scope) USP on settle so the server's
 * authoritative state — including `excludedByEmail`/`excludedByName` —
 * replaces the placeholder in-memory shape.
 *
 * **Not flag-gated.** The hook is a pure data-layer helper; gating belongs
 * at the call site (UI button) so unrelated callers cannot accidentally
 * bypass the rule. The corresponding API routes 404 when the `alert_ignore`
 * feature flag is disabled, so misuse fails closed even without UI gating.
 *
 * The `mute` fragment on each affected row is left untouched so a row that
 * is both excluded and muted keeps both states (defensive against any
 * future product decision to allow stacking). Mute and exclude are
 * orthogonal at the data layer.
 */
export function useExcludeAlert(
  scope: AlertExcludeScope
): UseMutationResult<
  ExcludedAlertState,
  Error,
  ExcludeAlertVariables,
  CacheSnapshot
> {
  const queryClient = useQueryClient();

  return useMutation<
    ExcludedAlertState,
    Error,
    ExcludeAlertVariables,
    CacheSnapshot
  >({
    mutationFn: ({ alertKey, excludedUntil }) => {
      if (scope.kind === 'organization') {
        return excludeOrganizationAlert(
          apiClient,
          scope.organizationId,
          alertKey,
          { excludedUntil }
        );
      }
      return excludeAccountAlert(apiClient, alertKey, { excludedUntil });
    },
    onMutate: async ({ alertKey, excludedUntil }) => {
      await cancelOverlappingQueries(queryClient, scope, alertKey);
      return applyOptimisticPatch(queryClient, scope, alertKey, {
        fragment: 'exclude',
        value: buildOptimisticExclude(excludedUntil),
      });
    },
    onError: (_error, _variables, context) => {
      if (context) {
        restoreSnapshot(queryClient, context);
      }
    },
    onSettled: (_data, _error, variables) => {
      invalidateAffectedQueries(
        queryClient,
        scope,
        variables.alertKey,
        'exclude'
      );
    },
  });
}

/**
 * React Query mutation hook to remove the exclusion from a grouped alert.
 *
 * Optimistically replaces the alert's `exclude` fragment with
 * {@link NOT_EXCLUDED_DEFAULT} in every grouped-alerts list and
 * alert-occurrences cache entry in scope, captures snapshots, and rolls
 * them back on error. Always invalidates lists, occurrences, and (org-scope)
 * USP on settle.
 *
 * **Not flag-gated** — see {@link useExcludeAlert}.
 */
export function useRemoveAlertExclusion(
  scope: AlertExcludeScope
): UseMutationResult<
  ExcludedAlertState,
  Error,
  RemoveAlertExclusionVariables,
  CacheSnapshot
> {
  const queryClient = useQueryClient();

  return useMutation<
    ExcludedAlertState,
    Error,
    RemoveAlertExclusionVariables,
    CacheSnapshot
  >({
    mutationFn: ({ alertKey }) => {
      if (scope.kind === 'organization') {
        return removeOrganizationAlertExclusion(
          apiClient,
          scope.organizationId,
          alertKey
        );
      }
      return removeAccountAlertExclusion(apiClient, alertKey);
    },
    onMutate: async ({ alertKey }) => {
      await cancelOverlappingQueries(queryClient, scope, alertKey);
      return applyOptimisticPatch(queryClient, scope, alertKey, {
        fragment: 'exclude',
        value: NOT_EXCLUDED_DEFAULT,
      });
    },
    onError: (_error, _variables, context) => {
      if (context) {
        restoreSnapshot(queryClient, context);
      }
    },
    onSettled: (_data, _error, variables) => {
      invalidateAffectedQueries(
        queryClient,
        scope,
        variables.alertKey,
        'exclude'
      );
    },
  });
}
